// ============================================================================
// taskCompletionLock — cross-client dedup for TASK 0→100% completion events
// ----------------------------------------------------------------------------
// Phase 4 hardening. Without this, when a TASK element crosses 100%, every
// online teammate's `GamificationContext` task watcher fires
// `TRACK_FINISH_TASK` + `WorkAction('TASK_COMPLETE')` — over-applying the 80
// damage and granting tickets N times.
//
// The fix: stamp a `workspaces/{workspaceId}/task_completion_locks/{taskId}`
// doc inside a runTransaction. Whoever wins the race writes; everyone else
// reads-then-aborts. The first transaction commits the lock and returns true;
// concurrent ones see the existing doc and return false.
//
// SCOPE (per the user's Step 2 verdict): the lock dedupes the gamification
// emissions ONLY — no credit minting, no daily cap. The existing
// `creditTaskCompletionReward` flow on checklist NODE completion is untouched.
// ============================================================================

import { doc, runTransaction, serverTimestamp, type Firestore } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import {
  incrementProgressInTransaction,
  type ProgressDelta,
} from './quotaService'

export const TASK_COMPLETION_LOCK_SUBCOLLECTION = 'task_completion_locks'

export const getTaskCompletionLockRef = (
  db: Firestore,
  workspaceId: string,
  taskId: string,
) => doc(db, 'workspaces', workspaceId, TASK_COMPLETION_LOCK_SUBCOLLECTION, taskId)

/**
 * Attempts to claim the TASK-completion lock for `taskId` in `workspaceId`.
 *
 * Returns true exactly once across all clients observing the same 0→100%
 * crossing — the caller that wins should emit the gamification events.
 *
 * Returns false if the lock was already claimed (silent no-op for losers), or
 * if Firestore rejects the write. The caller should never throw or surface a
 * failed-claim to the user — gamification dedup is best-effort, and a missed
 * grant is strictly better than over-granting raid damage.
 */
export interface QuotaCompletionContext {
  assigneeId: string
  delta: ProgressDelta
}

export const claimTaskCompletionLock = async (
  workspaceId: string,
  taskId: string,
  claimantUid: string,
  options: { db?: Firestore; quotaContext?: QuotaCompletionContext } = {},
): Promise<boolean> => {
  if (!workspaceId || !taskId || !claimantUid) return false
  const db = options.db ?? firestoreDb
  const lockRef = getTaskCompletionLockRef(db, workspaceId, taskId)

  try {
    return await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(lockRef)
      if (snapshot.exists()) return false
      // Quota increment first: if the quota write throws, neither the lock
      // nor the increment will commit — per ratified spec, the task
      // completion's gamification effects revert with the failed quota.
      if (options.quotaContext) {
        await incrementProgressInTransaction(transaction, {
          db,
          workspaceId,
          assigneeId: options.quotaContext.assigneeId,
          delta: options.quotaContext.delta,
        })
      }
      transaction.set(lockRef, {
        workspaceId,
        taskId,
        claimedBy: claimantUid,
        claimedAt: serverTimestamp(),
      })
      return true
    })
  } catch (error) {
    console.warn('[taskCompletionLock] claim failed', { workspaceId, taskId }, error)
    return false
  }
}

